import { Box, Button, Collapse, Typography, Chip, Stack, Alert, IconButton, Tooltip, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { TableDisplay } from '../../../../../presentation-components/miscellaneous-components/table-display'
import { LoadingSpinner } from '../../../../../presentation-components/display-elements-components/loading-spinner'
import { useValidationSummary } from '../../../../../../hooks/use-validation-summary'
import { IHeadCell } from '../../../../../../shared/interfaces/table-display.model'
import { IValidationError } from '../../../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model'
import { useState, useEffect, useContext, useRef, useMemo, FunctionComponent } from 'react'
import { EditOutlined, ExpandLessOutlined, ExpandMoreOutlined, HighlightOff } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { ValidationResultsStepCard } from '../../../../../presentation-components/settings-components/data-import/validation-results-step-card'
import { EditBulkUploadLinesDialog } from '../../../../../dialogs/settings/data-import/edit-bulk-upload-lines'
import { EditUploadLineEmissionDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-emission-dialog'
import { EditUploadLineVehicleDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-vehicle-dialog'
import { EditUploadLineSpendBasedDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-spend-based-dialog'
import { EditUploadLineGoodEmissionDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-good-emission-dialog'
import { EditUploadLineGoodDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-good-dialog'
import { EditUploadLineDirectEmissionDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-direct-emission-dialog'
import { EditUploadLineLocationDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-location-dialog'
import { EditUploadLineProjectDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-project-dialog'
import { EditUploadLineSupplierDialog } from '../../../../../dialogs/settings/data-import/edit-upload-line-supplier-dialog'
import { NominalCodeUploadMappingSection } from '../../../../../presentation-components/settings-components/data-import/nominal-code-upload-mapping-section'
import NominalCodeMappingService from '../../../../../../shared/services/nominal-code-mapping/nominal-code-mapping.service'
import { INominalSummaryResponse } from '../../../../../../shared/services/nominal-code-mapping/nominal-code-mapping.model'
import ManufacturerService from '../../../../../../shared/services/manufacturer/manufacturer.service'
import FileUploadAllLineService from '../../../../../../shared/services/file-upload-all-lines/file-upload-all-lines.service'
import { IManufacturer } from '../../../../../../shared/services/manufacturer/manufacturer.model'
import { UserNotificationsContext } from '../../../../../../context/user-notifications/user-notifications'
import { useBillDocuments } from '../../../../../../hooks/use-bill-documents'
import { BillReview } from '../../../../../presentation-components/settings-components/data-import/bill-review'

export const ValidationResultsStep: FunctionComponent<{
  uploadResult: any
  typeOfData: string,
  onRefreshValidation?: () => void
  onCanImportChange?: (canImport: boolean) => void
  onHasFetchedPendingErrorsChange?: (hasFetched: boolean) => void
  onFailedCountChange?: (failedCount: number) => void
  onValidCountChange?: (validCount: number) => void
}> = ({ uploadResult, typeOfData, onRefreshValidation, onCanImportChange, onHasFetchedPendingErrorsChange, onFailedCountChange, onValidCountChange }) => {
  //Services
  const fileUploadAllLineService = new FileUploadAllLineService()
  const manufacturerService = new ManufacturerService()
  const nominalCodeMappingService = new NominalCodeMappingService()
  // Context
  const userNotificationsContext = useContext(UserNotificationsContext);
  //Refs
  const shouldRefreshOnCloseRef = useRef<boolean>(false)
  const idToExcelRowRef = useRef<Record<string, number>>({})
  const validationRecordsRef = useRef<any[]>([])
  //State
  const [manufacturers, setManufacturers] = useState<IManufacturer[]>([])
  const [validationRecords, setValidationRecords] = useState<any[]>([])
  const [selected, setSelected] = useState<string[]>([]);
  const [pendingUploadLines, setPendingUploadLines] = useState<any[]>([])
  const [isDiscardSubmitting, setIsDiscardSubmitting] = useState(false)
  const [perPage, setPerPage] = useState(10)
  const [inputPage, setInputPage] = useState(0);
  const [hasFetchedPendingErrors, setHasFetchedPendingErrors] = useState(false)
  const [totalPendingCount, setTotalPendingCount] = useState<number>(0)
  const [hasPageMounted, setHasPageMounted] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [openBulkEditDialog, setOpenBulkEditDialog] = useState(false)
  // The inline Chart of Accounts table under the "codes have no mapping yet" prompt. Once opened it
  // stays mounted and only collapses, so hiding it never throws away outcomes not yet saved.
  const [showNominalMapping, setShowNominalMapping] = useState(false)
  const [hasOpenedNominalMapping, setHasOpenedNominalMapping] = useState(false)
  // Every code this spend upload carries, with its saved outcome, from the Chart of Accounts summary.
  // Drives an automatic prompt so the user only sees the mapping table when there is something to
  // do, and feeds that table, so the prompt's count and the table's rows always agree.
  const [nominalSummary, setNominalSummary] = useState<INominalSummaryResponse | null>(null)
  const [isNominalSummaryLoading, setIsNominalSummaryLoading] = useState<boolean>(false)
  const [nominalLoaded, setNominalLoaded] = useState<boolean>(false)
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false)
  //Hooks
  const { summary: hookSummary } = useValidationSummary(uploadResult, typeOfData, refreshTrigger)

  // For spend uploads, work out how many codes still need a decision. Re-runs after the inline table
  // saves (refreshTrigger), so the prompt clears itself once every code is mapped.
  useEffect(() => {
    if (typeOfData !== 'spend-based' || !uploadResult?.id) return
    let cancelled = false
    setIsNominalSummaryLoading(true)
    nominalCodeMappingService.getSummary({ fileUploadId: uploadResult.id })
      .then(res => {
        if (cancelled) return
        setNominalSummary(res)
        setIsNominalSummaryLoading(false)
        setNominalLoaded(true)
      })
      .catch(err => {
        console.log(err)
        if (cancelled) return
        setIsNominalSummaryLoading(false)
        setNominalLoaded(true)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOfData, uploadResult?.id, refreshTrigger])

  // The summary lists every saved mapping as well, so it is narrowed to the codes on this upload.
  const uploadNominalCodes = useMemo(
    () => (nominalSummary?.codes || []).filter(row => (row.origins || []).includes('upload')),
    [nominalSummary],
  )
  const nominalTotalCodes = uploadNominalCodes.length
  const nominalUnmappedCount = uploadNominalCodes.filter(row => !row.mapping).length

  // Once counts + pending rows (and, for spend, the nominal summary) have all resolved, latch a
  // "loaded" flag. The review body renders behind this single gate so the cards, alerts and table
  // appear together instead of popping in one at a time (which reads as a flicker/reflow).
  useEffect(() => {
    if (hasFetchedPendingErrors && hookSummary && (typeOfData !== 'spend-based' || nominalLoaded)) {
      setHasLoadedOnce(true)
    }
  }, [hasFetchedPendingErrors, hookSummary, typeOfData, nominalLoaded])
  // Edit states
  const [openEmissionDialog, setOpenEmissionDialog] = useState(false)
  const [openVehicleDialog, setOpenVehicleDialog] = useState(false)
  const [openSpendBasedDialog, setOpenSpendBasedDialog] = useState(false)
  const [openGoodEmissionDialog, setOpenGoodEmissionDialog] = useState(false)
  const [openGoodDialog, setOpenGoodDialog] = useState(false)
  const [openDirectEmissionDialog, setOpenDirectEmissionDialog] = useState(false)
  const [openLocationDialog, setOpenLocationDialog] = useState(false)
  const [openProjectDialog, setOpenProjectDialog] = useState(false)
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false)
  const [selectedEditRecord, setSelectedEditRecord] = useState<any>(null)
  const [editUploadLineValidationErrors, setEditUploadLineValidationErrors] = useState<IValidationError[] | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [errorCode, setErrorCode] = useState<number>()
  const [selectedFilterField, setSelectedFilterField] = useState<string>('')
  const [filteredRecords, setFilteredRecords] = useState<any[]>([])
  //Variables
  const detailedMessage = (uploadResult as any)?.detailedMessage
  const filteredTotalCount = selectedFilterField && filteredRecords ? filteredRecords.length : totalPendingCount
  const isLinkedActivityImport = uploadResult?.linkToExistingEmission === true
  const isBillImport = uploadResult?.sourceType === 'BILL_DOCUMENTS'
    && (typeOfData === 'emissions-rules' || typeOfData === 'emissions-manual')
  // The upload's bills, polled while any is still being read. When the last one finishes the rows
  // exist, so refetch them: a user who reached this step mid-read would otherwise sit on an empty
  // table until they reloaded the page by hand.
  const { documents: billDocuments, progress: billProgress, isReading: isBillExtracting, refresh: refreshBills } = useBillDocuments(
    isBillImport ? uploadResult?.id : null,
    () => {
      if (typeOfData) getPendingValidationErrors(typeOfData)
      setRefreshTrigger(prev => prev + 1)
    },
  )
  // After anything changes on the bill review: refetch the bills and the counts the wizard imports by.
  const refreshBillsAndRows = () => {
    refreshBills()
    if (typeOfData) getPendingValidationErrors(typeOfData)
    setRefreshTrigger(prev => prev + 1)
  }

  const normaliseValidationErrors = (errors: any): any[] => {
    if (Array.isArray(errors)) return errors
    if (typeof errors === 'string') {
      try {
        const parsed = JSON.parse(errors)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }
    if (errors && typeof errors === 'object') {
      return Object.values(errors).flat().filter(Boolean)
    }
    return []
  }

  useEffect(() => {
    manufacturerService.getAll()
      .then(response => {
        setManufacturers(response)
      })
      .catch(() => { })
  }, [])

  useEffect(() => {
    if (!uploadResult.id || !typeOfData) {
      setHasFetchedPendingErrors(false)
      return
    }
    setHasFetchedPendingErrors(false)
    getPendingValidationErrors(typeOfData)
  }, [uploadResult.id, typeOfData, inputPage, perPage])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [typeOfData])


  const idToExcelRow = useMemo(() => {
    const map: Record<string, number> = {}
    if (uploadResult?.parsedData && Array.isArray(uploadResult.parsedData)) {
      uploadResult.parsedData.forEach((item: any, idx: number) => {
        if (item?.id != null) {
          const baseIndex = typeof item.index === 'number' ? item.index : idx
          map[item.id] = baseIndex + 2
        }
      })
    }
    if (Array.isArray(detailedMessage)) {
      detailedMessage.forEach(entry => {
        if (entry && entry.id && typeof entry.excelRow === 'number') {
          map[entry.id] = entry.excelRow
        }
      })
    }
    return map
  }, [uploadResult?.parsedData, detailedMessage])

  useEffect(() => {
    if (idToExcelRow && Object.keys(idToExcelRow).length > 0) {
      idToExcelRowRef.current = { ...idToExcelRowRef.current, ...idToExcelRow }
    }
  }, [idToExcelRow])

  useEffect(() => {
    const map = idToExcelRowRef.current
    if (!map || Object.keys(map).length === 0) return
    if (!Array.isArray(validationRecords) || validationRecords.length === 0) return
    let changed = false
    const next = validationRecords.map((record: any) => {
      const desired = map[record.id]
      if (typeof desired === 'number' && desired !== record.row) {
        changed = true
        return { ...record, row: desired }
      }
      return record
    })
    if (changed) setValidationRecords(next)
  }, [validationRecords, idToExcelRow])

  const sortedValidationRecords = useMemo(() => {
    if (!Array.isArray(validationRecords)) return []
    return [...validationRecords].sort((a: any, b: any) => {
      const ra = typeof a.row === 'number' ? a.row : parseInt(String(a.row || '0'), 10) || 0
      const rb = typeof b.row === 'number' ? b.row : parseInt(String(b.row || '0'), 10) || 0
      return ra - rb
    })
  }, [validationRecords])

  const getUniqueValidationErrorFields = (): string[] => {
    if (!sortedValidationRecords || sortedValidationRecords.length === 0) {
      return []
    }
    const fieldSet = new Set<string>()
    sortedValidationRecords.forEach((record: any) => {
      const validationErrors = normaliseValidationErrors(record.validationErrors)
      if (validationErrors.length > 0) {
        validationErrors.forEach((error: any) => {
          if (error.field && error.field.trim() !== '') {
            fieldSet.add(error.field)
          }
        })
      }
    })
    return Array.from(fieldSet).sort()
  }

  useEffect(() => {
    if (!sortedValidationRecords || sortedValidationRecords.length === 0 || !selectedFilterField) {
      setFilteredRecords(sortedValidationRecords || [])
      return
    }
    const filtered = sortedValidationRecords.filter((record: any) =>
      normaliseValidationErrors(record.validationErrors).some((error: any) => error.field === selectedFilterField)
    )
    setFilteredRecords(filtered)
  }, [sortedValidationRecords, selectedFilterField])

  const failedDistinct = useMemo(() => {
    // For bills the fetched records include valid rows, so only the ones carrying errors count as
    // failed. Reporting every row as failed would trip the wizard's "Invalid Records Detected"
    // confirm on a perfectly clean import.
    // For spreadsheets, use the fetch's total pending count, NOT the fetched list's length: the
    // list is page-capped (perPage rows), so a 100-row upload with 37 failures would report 10
    // failed until the summary hook resolved, and the wizard's partial-import confirm reads this.
    const localCount = isBillImport
      ? sortedValidationRecords.filter((record: any) => normaliseValidationErrors(record.validationErrors).length > 0).length
      : totalPendingCount
    if (uploadResult?.id) {
      return localCount
    }
    if (hookSummary && typeof hookSummary.failed === 'number') return hookSummary.failed
    return localCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookSummary, sortedValidationRecords, totalPendingCount, uploadResult?.id, isBillImport])

  useEffect(() => {
    const total = (hookSummary && typeof hookSummary.total === 'number')
      ? hookSummary.total
      : (Array.isArray(uploadResult?.parsedData) ? uploadResult.parsedData.length : 0)
    const failed = (hookSummary && typeof hookSummary.failed === 'number')
      ? hookSummary.failed
      : failedDistinct
    const success = (hookSummary && typeof hookSummary.success === 'number')
      ? hookSummary.success
      : Math.max(0, total - failed)
    const canImport = total > 0
    if (onCanImportChange) onCanImportChange(canImport)
    if (onFailedCountChange) onFailedCountChange(failed)
    if (onValidCountChange) onValidCountChange(success)
  }, [hookSummary, uploadResult?.parsedData, failedDistinct, onCanImportChange, onFailedCountChange, onValidCountChange])

  //Use Effect Helpers
  const getPendingValidationErrors = async (fileType: string) => {
    if (!uploadResult?.id) return
    try {
      // Bill rows were read off a document, not typed by the user, so the review lists EVERY
      // staged row ('reconciliation' = valid and invalid alike) for an eyeball check. Spreadsheet
      // imports keep the original behaviour: only the rows that failed validation ('pending').
      const lineStatus = isBillImport ? 'reconciliation' : 'pending'
      const resp = await fileUploadAllLineService.getUploadLinesWithPaginationByFile(typeOfData, uploadResult.id, lineStatus, inputPage, perPage)
      setTotalPendingCount(resp.count)
      if (Array.isArray(resp?.rows) && resp.rows.length > 0) {
        const mapped = mapServerRowsToRecords(resp.rows, typeOfData, inputPage, perPage)
        setValidationRecords(mapped)
        validationRecordsRef.current = mapped
      } else if (Array.isArray(resp?.rows) && resp.rows.length === 0) {
        setValidationRecords([])
        validationRecordsRef.current = []
      }

      setHasFetchedPendingErrors(true)
      onHasFetchedPendingErrorsChange?.(true)
    } catch (error: any) {
      console.log(error)
      setHasFetchedPendingErrors(true)
      onHasFetchedPendingErrorsChange?.(true)
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    }
  }

  //Use Effect Helpers Helpers
  const mapServerRowsToRecords = (rows: any[], fileType: string, currentPage: number, perPage: number = 10) => {
    return rows.map((row: any, rowIdx: number) => {
      const rowNumber = typeof row.rowNumber === 'number' ? row.rowNumber : undefined
      const validationErrors = normaliseValidationErrors(row.validationErrors)
      return {
        id: row.id,
        row: rowNumber,
        contactName: row.contactName,
        fullRow: row,
        validationErrors: validationErrors,
        linkStatus: row.linkStatus,
        matchReasons: row.matchReasons || []
      }
    })
  }

  const mapValidationRecordsToTableRows = (records: any[]) => {
    return records.map((record: any) => {
      const validationErrors = normaliseValidationErrors(record.validationErrors)
      const displayErrors = validationErrors
      const fieldList = displayErrors.length > 0 ? (
        <Stack spacing={0.5}>
          {displayErrors.map((ve: any, idx: number) => (
            <div key={idx}>{ve.field || 'Unknown'}</div>
          ))}
        </Stack>
      ) : ''
      const errorList = displayErrors.length > 0 ? (
        <Stack spacing={0.5}>
          {displayErrors.map((ve: any, idx: number) => {
            const errorText = ve.message || 'Validation failed'
            const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
            return <div key={idx}>{truncatedError}</div>
          })}
        </Stack>
      ) : ''
      const severityList = displayErrors.length > 0 ? (
        <Stack spacing={0.5}>
          {displayErrors.map((ve: any, idx: number) => (
            <Chip
              key={idx}
              color={ve.severity === 'warning' ? 'warning' : 'error'}
              label={ve.severity === 'warning' ? 'Warning' : 'Error'}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      ) : ''

      return {
        id: record.id,
        row: record.row || '',
        supplier: record.contactName || '',
        field: fieldList,
        error: errorList,
        severity: severityList,
        action: (
          <Tooltip title="Edit" placement='top'>
            <IconButton
              onClick={() => handleEditClick(record.id)}
              size="small">
              <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ),
      }
    })
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'row', numeric: true, label: 'Row', sortable: false },
    ...(typeOfData === 'spend-based' ? [{ id: 'supplier', numeric: false, label: 'Supplier', sortable: false }] : []),
    { id: 'field', numeric: false, label: 'Field', sortable: false },
    { id: 'error', numeric: false, label: 'Error', sortable: false },
    { id: 'severity', numeric: false, label: 'Severity', sortable: false },
  ]

  //Edit Dialog Handlers
  const handleEditClick = (lineId: string) => {
    const record = validationRecordsRef.current.find((rec: any) => rec.id === lineId)
    if (record?.fullRow) {
      setSelectedEditRecord(record.fullRow)
      const fileType = typeOfData
      if (fileType) {
        openDialogForType(fileType)
      }
    }
  }

  const openDialogForType = (dataType: string) => {
    const dialogMap: Record<string, () => void> = {
      'emissions-rules': () => setOpenEmissionDialog(true),
      'emissions-manual': () => setOpenEmissionDialog(true),
      'emissions-direct': () => setOpenDirectEmissionDialog(true),
      'emissions-product': () => setOpenGoodEmissionDialog(true),
      'spend-based': () => setOpenSpendBasedDialog(true),
      'vehicles': () => setOpenVehicleDialog(true),
      'products': () => setOpenGoodDialog(true),
      'locations': () => setOpenLocationDialog(true),
      'projects': () => setOpenProjectDialog(true),
      'suppliers': () => setOpenSupplierDialog(true)
    }
    dialogMap[dataType] ? dialogMap[dataType]() : setOpenEmissionDialog(true)
  }

  const closeDialogHandler = () => {
    setOpenEmissionDialog(false)
    setOpenVehicleDialog(false)
    setOpenSpendBasedDialog(false)
    setOpenGoodEmissionDialog(false)
    setOpenGoodDialog(false)
    setOpenDirectEmissionDialog(false)
    setOpenLocationDialog(false)
    setOpenProjectDialog(false)
    setSelectedEditRecord(null)
    setEditUploadLineValidationErrors(undefined)
    if (shouldRefreshOnCloseRef.current && onRefreshValidation) {
      onRefreshValidation()
    }
    shouldRefreshOnCloseRef.current = false
  }

  const handleEditSubmit = async (lineId: string, editUploadLine: any) => {
    setErrorCode(undefined)
    setErrorMessage(undefined)
    try {
      if (!lineId) {
        throw new Error('No line Id found')
      }
      if (!typeOfData) {
        throw new Error('No file type found')
      }
      await fileUploadAllLineService.editUploadLine(editUploadLine, lineId, typeOfData)
      await getPendingValidationErrors(typeOfData)
      setRefreshTrigger(prev => prev + 1)
      setSelectedFilterField('')
      shouldRefreshOnCloseRef.current = true
      closeDialogHandler()
    } catch (error: any) {
      if (error.response.data.code && error.response.data.message) {
        if (error.response.data.code === 100) {
          userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
        } else {
          setErrorCode(error.response.data.code)
          setErrorMessage(error.response.data.message)
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        }
      } else {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      }
    }
  }

  const handleSelected = (selectedRowId: string[]) => {
    const formatted = selectedRowId.map(row => {
      return {
        id: row
      }
    })
    setPendingUploadLines(formatted)
  }

  const deleteMultipleUploadLine = () => {
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to remove this record(s)? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response && uploadResult?.id && typeOfData && pendingUploadLines && pendingUploadLines.length > 0) {
          if (!isDiscardSubmitting) {
            setIsDiscardSubmitting(true)
            fileUploadAllLineService.deleteUploadLines(pendingUploadLines, typeOfData)
              .then(response => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Discarded successfully' });
                setSelected([])
                setPendingUploadLines([])
                setSelectedFilterField('')
                getPendingValidationErrors(typeOfData)
                setRefreshTrigger(prev => prev + 1)
                onRefreshValidation?.()
                setIsDiscardSubmitting(false)
              })
              .catch((err) => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                setIsDiscardSubmitting(false)
                console.log(err)
              })
          }
        }
      })
  }

  const openBulkEditDialogHandler = () => {
    if (pendingUploadLines && pendingUploadLines.length > 0) {
      setOpenBulkEditDialog(true)
    }
  }

  const closeBulkEditDialogHandler = () => {
    setOpenBulkEditDialog(false)
  }

  const successBulkEditDialogHandler = () => {
    setSelected([])
    setPendingUploadLines([])
    setSelectedFilterField('')
    if (typeOfData) {
      getPendingValidationErrors(typeOfData)
      setRefreshTrigger(prev => prev + 1)
      onRefreshValidation?.()
    }
    setOpenBulkEditDialog(false)
  }

  // After the inline table saves: the same refresh the step makes after any edit to its rows.
  const nominalMappingSavedHandler = () => {
    setSelectedFilterField('')
    getPendingValidationErrors(typeOfData)
    setRefreshTrigger(prev => prev + 1)
    onRefreshValidation?.()
  }

  const getBulkEditShowMenuItems = (fileType: string) => {
    if (fileType === 'emissions-direct') {
      return ['EDIT_GROUP', 'EDIT_UNITS_CONSUMED', 'EDIT_CATEGORY', 'EDIT_DATA_TYPE', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_ENERGY_KWH', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME']
    }
    if (fileType === 'emissions-product') {
      return ['EDIT_PRODUCT', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT']
    }
    if (fileType === 'spend-based') {
      return ['EDIT_GROUP', 'EDIT_ACTIVITY', 'EDIT_TOTAL', 'EDIT_INVOICE_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME', 'EDIT_BANK_ACCOUNT_NAME']
    }
    if (fileType === 'emissions-rules') {
      return ['EDIT_RULE', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME']
    }
    if (fileType === 'emissions-manual') {
      return ['EDIT_CONVERSION', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME', 'EDIT_SUPPLIER_EMISSION_RATE']
    }
    return []
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Review
      </Typography>
      {!isBillImport && (
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          {isLinkedActivityImport
            ? 'Review the validation results below. Fix invalid rows here, then continue to the link review step.'
            : 'Review the results below. Fix any errors before importing.'}
        </Typography>
      )}
      {uploadResult && isBillImport && uploadResult.id ? (
        // Bills get a to-do list instead of the spreadsheet's error table: what to do before the
        // rows can import, with the usage and dates typed in on the row.
        <BillReview
          fileUploadId={uploadResult.id}
          fileType={typeOfData}
          reportPeriodId={uploadResult.reportPeriodId}
          documents={billDocuments}
          progress={billProgress}
          isReading={isBillExtracting}
          onChanged={refreshBillsAndRows}
          context='wizard'
        />
      ) : uploadResult ? (
        !hasLoadedOnce ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <LoadingSpinner />
          </Box>
        ) : (
        <Stack spacing={3}>
          {hookSummary && (
            (() => {
              const total = (hookSummary && typeof hookSummary.total === 'number')
                ? hookSummary.total
                : (Array.isArray(uploadResult?.parsedData) ? uploadResult.parsedData.length : 0)
              const failed = (hookSummary && typeof hookSummary.failed === 'number')
                ? hookSummary.failed
                : failedDistinct
              const success = (hookSummary && typeof hookSummary.success === 'number')
                ? hookSummary.success
                : Math.max(0, total - failed)
              const summary = { total, failed, success }
              const recordsMatch = hookSummary && typeof hookSummary.total === 'number' &&
                typeof hookSummary.success === 'number' &&
                typeof hookSummary.failed === 'number' &&
                (hookSummary.success + hookSummary.failed) === hookSummary.total
              return (
                <Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'nowrap' }}>
                    <ValidationResultsStepCard
                      value={summary?.total || 0}
                      label="Total records"
                      color="text.primary"
                    />
                    <ValidationResultsStepCard
                      value={summary?.success || 0}
                      label="Valid records"
                      color="primary.main"
                    />
                    <ValidationResultsStepCard
                      value={summary?.failed || 0}
                      label={summary?.failed && summary.failed > 0
                        ? 'Records with errors'
                        : 'No errors'}
                      color={summary?.failed && summary.failed > 0 ? 'error.main' : 'text.primary'}
                    />
                  </Box>
                  {summary?.total === 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      No records were found in the uploaded file. The spreadsheet appears to be empty.
                    </Alert>
                  )}
                  {!recordsMatch && hookSummary && uploadResult?.id && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      The number of valid and error records does not match the total. Some records may not have been processed yet.
                    </Alert>
                  )}
                  {!isLinkedActivityImport && summary?.total > 0 && summary?.failed === 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      All records validated successfully. Ready to import.
                    </Alert>
                  )}
                  {isLinkedActivityImport && summary?.total > 0 && summary?.failed === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Validation is complete. Continue to review how valid rows will link to existing emissions before importing.
                    </Alert>
                  )}
                </Box>
              )
            })()
          )}
          {typeOfData === 'spend-based' && uploadResult?.pinnedConversionYear && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Factor year pinned to {uploadResult.pinnedConversionYear}. Every row in this upload was matched to the {uploadResult.pinnedConversionYear} factor set instead of its invoice date.
            </Alert>
          )}
          {typeOfData === 'spend-based' && Array.isArray(uploadResult?.fixedExchangeRates) && uploadResult.fixedExchangeRates.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {uploadResult.fixedExchangeRates.length === 1
                ? 'A fixed exchange rate is set for this reporting period, so spend converted between these currencies uses it instead of the daily market rate: '
                : 'Fixed exchange rates are set for this reporting period, so spend converted between these currencies uses them instead of the daily market rate: '}
              {uploadResult.fixedExchangeRates
                .map((rate: { fromCurrencyCode: string, toCurrencyCode: string, rate: number, rateSource: string }) =>
                  `1 ${rate.fromCurrencyCode} = ${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 6 }).format(rate.rate)} ${rate.toCurrencyCode} (${rate.rateSource})`)
                .join('; ')}.
            </Alert>
          )}
          {typeOfData === 'spend-based' && nominalUnmappedCount > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  aria-expanded={showNominalMapping}
                  startIcon={showNominalMapping ? <ExpandLessOutlined fontSize="small" /> : <ExpandMoreOutlined fontSize="small" />}
                  onClick={() => {
                    setHasOpenedNominalMapping(true)
                    setShowNominalMapping(prev => !prev)
                  }}>
                  <Typography variant="body2" sx={{ pl: 0.5 }}>{showNominalMapping ? 'Hide codes' : 'Map codes'}</Typography>
                </Button>
              }
            >
              {nominalUnmappedCount} nominal {nominalUnmappedCount === 1 ? 'code has' : 'codes have'} no mapping yet, so {nominalUnmappedCount === 1 ? 'its rows cannot' : 'their rows cannot'} be calculated. Map {nominalUnmappedCount === 1 ? 'it' : 'them'} once and every future import reuses it. Codes you have already mapped are applied automatically.
            </Alert>
          )}
          {typeOfData === 'spend-based' && nominalUnmappedCount > 0 && hasOpenedNominalMapping && uploadResult?.id && (
            <Collapse in={showNominalMapping}>
              <NominalCodeUploadMappingSection
                fileUploadId={uploadResult.id}
                summary={nominalSummary}
                isLoading={isNominalSummaryLoading}
                onSaved={nominalMappingSavedHandler}
              />
            </Collapse>
          )}
          {typeOfData === 'spend-based' && nominalTotalCodes > 0 && nominalUnmappedCount === 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              All {nominalTotalCodes} nominal {nominalTotalCodes === 1 ? 'code is' : 'codes are'} mapped, and applied automatically.
            </Alert>
          )}
          {hasFetchedPendingErrors && Boolean(sortedValidationRecords.length > 0 || failedDistinct > 0) ? (
            <Box>
              <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter</InputLabel>
                  <Select
                    label="Filter"
                    value={selectedFilterField}
                    onChange={(e) => {
                      setSelectedFilterField(e.target.value)
                      setInputPage(0)
                    }}
                    size="small"
                  >
                    <MenuItem value="">
                      <em>All fields</em>
                    </MenuItem>
                    {getUniqueValidationErrorFields().map((field) => (
                      <MenuItem key={field} value={field}>
                        {field}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={2}>
                  {(typeOfData === 'emissions-direct' ||
                    typeOfData === 'emissions-product' ||
                    typeOfData === 'emissions-rules' ||
                    typeOfData === 'emissions-manual' ||
                    typeOfData === 'spend-based') && (
                      <LoadingButton
                        onClick={openBulkEditDialogHandler}
                        disabled={pendingUploadLines?.length === 0}
                        startIcon={<EditOutlined />}
                        variant='contained'
                      >
                        <Typography>{`Edit selected (${selected.length})`}</Typography>
                      </LoadingButton>
                    )}
                  <LoadingButton
                    onClick={deleteMultipleUploadLine}
                    disabled={pendingUploadLines?.length === 0 || isDiscardSubmitting}
                    startIcon={<HighlightOff />}
                    variant='outlined'
                    loading={isDiscardSubmitting}
                    loadingPosition="start"
                  >
                    <Typography>{isDiscardSubmitting ? 'Removing selected' : 'Remove selected'}</Typography>
                  </LoadingButton>
                </Stack>
              </Stack>
              {(selectedFilterField ? filteredRecords : sortedValidationRecords).length > 0 && (
                <TableDisplay
                  headCells={headCells}
                  rows={mapValidationRecordsToTableRows(selectedFilterField ? filteredRecords : sortedValidationRecords)}
                  selectByIcon={true}
                  selectByCheckboxOnly={true}
                  haveCheckboxSelect={true}
                  totalRecords={filteredTotalCount}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  handleSelected={handleSelected}
                  selected={selected}
                  setSelected={setSelected}
                  isMobileMode={false}
                  isEmissionMobile={false}
                  isReportActionCard={false}
                />
              )}
            </Box>
          ) : null}
        </Stack>
        )
      ) : (
        <Alert severity="info">
          No validation results available. Please upload a file first.
        </Alert>
      )}
      {selectedEditRecord && (typeOfData === 'emissions-rules' || typeOfData === 'emissions-manual') && (
        <EditUploadLineEmissionDialog
          open={openEmissionDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
        />
      )}
      {selectedEditRecord && typeOfData === 'spend-based' && (
        <EditUploadLineSpendBasedDialog
          open={openSpendBasedDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
        />
      )}
      {selectedEditRecord && typeOfData === 'emissions-product' && (
        <EditUploadLineGoodEmissionDialog
          open={openGoodEmissionDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
        />
      )}
      {selectedEditRecord && typeOfData === 'emissions-direct' && (
        <EditUploadLineDirectEmissionDialog
          open={openDirectEmissionDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
        />
      )}
      {selectedEditRecord && typeOfData === 'vehicles' && (
        <EditUploadLineVehicleDialog
          open={openVehicleDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          validationErrors={editUploadLineValidationErrors}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
          errorMessage={errorMessage || ''}
          errorCode={errorCode || 0}
        />
      )}
      {selectedEditRecord && typeOfData === 'products' && (
        <EditUploadLineGoodDialog
          open={openGoodDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          manufacturers={manufacturers}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
          errorMessage={errorMessage || ''}
          errorCode={errorCode || 0}
        />
      )}
      {selectedEditRecord && typeOfData === 'locations' && (
        <EditUploadLineLocationDialog
          open={openLocationDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
          errorMessage={errorMessage || ''}
          errorCode={errorCode || 0}
        />
      )}
      {selectedEditRecord && typeOfData === 'projects' && (
        <EditUploadLineProjectDialog
          open={openProjectDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          submitHandler={handleEditSubmit}
          errorMessage={errorMessage || ''}
          errorCode={errorCode || 0}
          isSubmitting={false}
        />
      )}
      {selectedEditRecord && typeOfData === 'suppliers' && (
        <EditUploadLineSupplierDialog
          open={openSupplierDialog}
          closeHandler={closeDialogHandler}
          editUploadLine={selectedEditRecord}
          submitHandler={handleEditSubmit}
          isSubmitting={false}
          errorMessage={errorMessage || ''}
          errorCode={errorCode || 0}
        />
      )}
      {openBulkEditDialog &&
        (typeOfData === 'emissions-direct' ||
          typeOfData === 'emissions-product' ||
          typeOfData === 'emissions-rules' ||
          typeOfData === 'emissions-manual' ||
          typeOfData === 'spend-based') && (
          <EditBulkUploadLinesDialog
            open={openBulkEditDialog}
            closeHandler={closeBulkEditDialogHandler}
            dialogDetailsIds={pendingUploadLines.map(line => line.id)}
            successHandler={successBulkEditDialogHandler}
            fileType={typeOfData}
            showMenuItems={getBulkEditShowMenuItems(typeOfData)}
            reportPeriodId={uploadResult?.reportPeriodId}
            create={false}
          />
        )}
    </Box>
  )
}